/**
 * Loads theme custom elements on demand.
 *
 * Savor ships every component module as an unconditional `<script type="module">`
 * in the document head — around two dozen requests on a page that typically uses
 * four or five of them. Each of those modules exists only to register a custom
 * element, so the element's tag appearing in the DOM is an exact signal for
 * "this module is needed".
 *
 * The first scan runs once the document has parsed; after that a MutationObserver
 * catches markup that arrives later (section rendering, the cart drawer, quick
 * add). Observing is deliberately deferred until after parsing — attaching during
 * head parse would fire a callback for essentially every node in the document.
 *
 * The tag -> module URL map is rendered by `snippets/scripts.liquid` into a JSON
 * script tag, because only Liquid can resolve `asset_url`.
 */

const CONFIG_ID = 'lazy-components-manifest';

/** @type {Record<string, string>} */
let manifest = {};

try {
  manifest = JSON.parse(document.getElementById(CONFIG_ID)?.textContent || '{}');
} catch {
  manifest = {};
}

/** Tags still waiting to be seen. Shrinks as modules load. */
const pending = new Set(Object.keys(manifest));

/**
 * Loads every module whose tag is currently in the document.
 *
 * One `querySelectorAll` over the remaining tags rather than one query per tag:
 * this runs again on every mutation batch, so the per-call cost matters.
 */
function scan() {
  if (pending.size === 0) return true;

  const matches = document.querySelectorAll([...pending].join(','));

  for (const element of matches) {
    const tag = element.localName;
    if (!pending.delete(tag)) continue;

    // A failed component must not take the rest of the page down with it.
    import(manifest[tag]).catch((error) => {
      console.error(`Failed to load component "${tag}"`, error);
    });
  }

  return pending.size === 0;
}

function start() {
  if (scan()) return;

  // Mutation batches arrive far faster than modules can load, so collapse them
  // into one scan per frame.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      if (scan()) observer.disconnect();
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
